import React from 'react';

import type { RiskWithVulnerability } from '@/hooks/useRisks';
import { useRiskFrameworkRefs } from '@/sections/vulnerabilities/hooks/useRiskFrameworkRefs';
import {
  DEFAULT_BADGE_COLOR,
  FRAMEWORK_BADGE_COLORS,
} from '@/sections/vulnerabilities/utils/frameworkEnrichment.util';
import { cn } from '@/utils/classname';

const MAX_BADGES = 3;

interface FrameworksCellProps {
  risk: RiskWithVulnerability;
}

export const FrameworksCell: React.FC<FrameworksCellProps> = React.memo(
  ({ risk }) => {
    // risk.id is the attached Vulnerability's id (populated when a
    // vulnerability column, filter, or sort is active — see
    // needsVulnerabilityData, useRisks.ts);
    // riskCveId treats it exactly like the drawer's `vulnerabilityId` fallback.
    const badges = useRiskFrameworkRefs(risk.name, risk.id);

    if (badges.length === 0) {
      return <span className="text-chariot-text-secondary">-</span>;
    }

    const visible = badges.slice(0, MAX_BADGES);
    const overflow = badges.length - MAX_BADGES;

    return (
      <div className="flex items-center gap-1">
        {visible.map((b, i) => (
          <span
            key={i}
            className={cn(
              'inline-flex truncate rounded px-1.5 py-0.5 text-[10px] font-semibold',
              FRAMEWORK_BADGE_COLORS[b.framework] ?? DEFAULT_BADGE_COLOR
            )}
          >
            {b.id}
          </span>
        ))}
        {overflow > 0 && (
          <span className="text-chariot-text-secondary text-[10px]">
            +{overflow}
          </span>
        )}
      </div>
    );
  }
);

FrameworksCell.displayName = 'FrameworksCell';
